import { Chart, useChart } from '@chakra-ui/charts';
import { AbsoluteCenter, Box, Em, HStack, Heading, SimpleGrid, Skeleton, Stat, Status, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { Cell, LabelList, Pie, PieChart, Tooltip } from 'recharts';

import { type MainStat, type PerCommitmentStat, type PerPillarStat, StatsService } from '@/client';

const hashUUIDToNumber = (uuid: string) => {
  const hash = uuid.split('').reduce((acc, char) => {
    return (acc << 5) - acc + char.charCodeAt(0); // Simple hash function
  }, 0);
  return Math.abs(hash);
};

const getColorForUUID = (uuid: string, shade = '500') => {
  const colors = ['teal', 'purple', 'blue', 'green', 'red', 'yellow', 'orange', 'pink'];
  const hashValue = hashUUIDToNumber(uuid);
  const colorIndex = hashValue % colors.length;
  return `${colors[colorIndex]}.${shade}`;
};

const StatsCards = ({ statsData, isLoading }: { statsData: MainStat | undefined; isLoading: boolean }) => {
  if (!statsData) {
    return null;
  }

  return (
    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} gap={4} px={4}>
      {/* Card for Actions Count */}
      <Stat.Root borderWidth="1px" p="4" rounded="md" bg="primary.500">
        <Stat.Label fontSize="lg" color={'white'}>
          Nombre d'actions
        </Stat.Label>
        <Skeleton width="full" loading={isLoading}>
          <HStack>
            <Stat.ValueText textWrap={'nowrap'} fontSize="3xl" color={'white'}>
              {statsData.actions_count}
            </Stat.ValueText>
            <Stat.HelpText color={'white'}>
              {(statsData.last_month_new_actions_count || 0) > 0 && <Stat.UpIndicator color={'white'} />}
              {statsData.last_month_new_actions_count} new since last month
            </Stat.HelpText>
          </HStack>
        </Skeleton>
      </Stat.Root>

      {/* Card for Indicators Count */}
      <Stat.Root borderWidth="1px" p="4" rounded="md" bg="primary.500">
        <Stat.Label fontSize="lg" color={'white'}>
          Nombre d'indicateurs
        </Stat.Label>
        <Skeleton width="full" loading={isLoading}>
          <HStack>
            <Stat.ValueText textWrap={'nowrap'} fontSize="3xl" color={'white'}>
              {statsData.indicators_count}
            </Stat.ValueText>
            <Stat.HelpText color={'white'}>
              {(statsData.last_month_new_actions_count || 0) > 0 && <Stat.UpIndicator color={'white'} />}
              {statsData.last_month_new_indicators_count} new since last month
            </Stat.HelpText>
          </HStack>
        </Skeleton>
      </Stat.Root>

      {/* Card for Members Count */}
      <Stat.Root borderWidth="1px" p="4" rounded="md" bg="primary.500">
        <Stat.Label fontSize="lg" color={'white'}>
          Nombre d'utilisateurs
        </Stat.Label>
        <Skeleton width="full" loading={isLoading}>
          <HStack>
            <Stat.ValueText textWrap={'nowrap'} fontSize="3xl" color={'white'}>
              {statsData.members_count}
            </Stat.ValueText>
            <Stat.HelpText color={'white'}>
              {(statsData.last_month_new_actions_count || 0) > 0 && <Stat.UpIndicator color={'white'} />}
              {statsData.last_month_new_members_count} new since last month
            </Stat.HelpText>
          </HStack>
        </Skeleton>
      </Stat.Root>
    </SimpleGrid>
  );
};

type CommonKey = keyof PerPillarStat & keyof PerCommitmentStat;

const DonutsChartsDistribution = ({
  perPillars,
  perCommitments,
  commonKey,
  title,
}: {
  perPillars: PerPillarStat[];
  perCommitments: PerCommitmentStat[];
  commonKey: CommonKey;
  title: string;
}) => {
  const chartCommitments = useChart({
    data: perCommitments
      .sort((a, b) => a.pillar_id.localeCompare(b.pillar_id))
      .map((item) => ({
        name: item.commitment_name,
        value: item[commonKey],
        color: getColorForUUID(item.pillar_id, '500'),
      })),
  });
  const chartPillars = useChart({
    data: perPillars
      .sort((a, b) => a.pillar_id.localeCompare(b.pillar_id))
      .map((item) => ({
        name: item.pillar_name,
        value: item[commonKey],
        color: getColorForUUID(item.pillar_id, '400'),
      })),
  });

  const noDataAvailable = chartCommitments.data.length === 0 || chartCommitments.data.every((item) => item.value === 0);

  if (noDataAvailable) {
    return (
      <Box pos="relative" w="full" h="full">
        <Heading size={'lg'} mb={4}>
          {title}
        </Heading>
        <AbsoluteCenter>
          <Text>
            <Em>No data available</Em>
          </Text>
        </AbsoluteCenter>
      </Box>
    );
  }
  return (
    <>
      <Heading size={'lg'} mb={4}>
        {title}
      </Heading>
      <Chart.Root boxSize="400px" chart={chartPillars} mx="auto">
        <PieChart>
          <Tooltip cursor={false} animationDuration={100} content={<Chart.Tooltip hideLabel />} />
          <Pie
            innerRadius={80}
            outerRadius={160}
            isAnimationActive={false}
            data={chartCommitments.data}
            dataKey={chartCommitments.key('value')}
          >
            <LabelList position="inside" fill="white" stroke="none" />

            {chartCommitments.data.map((item) => {
              return <Cell key={item.name} fill={chartCommitments.color(item.color)} />;
            })}
          </Pie>
          <Pie
            innerRadius={0}
            outerRadius={80}
            isAnimationActive={false}
            data={chartPillars.data}
            dataKey={chartPillars.key('value')}
          >
            <LabelList position="inside" fill="white" stroke="none" />
            {chartPillars.data.map((item) => {
              return <Cell key={item.name} fill={chartPillars.color(item.color)} />;
            })}
          </Pie>
        </PieChart>
      </Chart.Root>
      <Box>
        <HStack gap="4" maxW="full" wrap={'wrap'} justifyContent={'center'}>
          {perPillars
            .sort((a, b) => a.pillar_id.localeCompare(b.pillar_id))
            .map((item) => (
              <HStack key={item.pillar_id}>
                <Status.Root colorPalette={getColorForUUID(item.pillar_id).split('.')[0]}>
                  <Status.Indicator />
                </Status.Root>
                <Text key={item.pillar_id} fontSize={'xs'}>
                  {item.pillar_name}
                </Text>
              </HStack>
            ))}
        </HStack>
      </Box>
    </>
  );
};

function getMainStatsQueryOptions({ slug }: { slug: string }) {
  return {
    queryKey: ['stats', { slug }],
    queryFn: async () => {
      return await StatsService.readMainStats({
        companySlug: slug,
      });
    },
  };
}

export const DashboardMainView = () => {
  const { slug } = useParams({ strict: false });

  const { data: statsData, isLoading } = useQuery({
    ...getMainStatsQueryOptions({
      slug,
    }),
  });

  return (
    <>
      <StatsCards statsData={statsData} isLoading={isLoading} />
      <SimpleGrid columns={{ base: 1, lg: 2 }} gap="4" p="4">
        <Box bg="white" borderWidth="1px" p="4" rounded="md">
          <DonutsChartsDistribution
            perPillars={statsData?.per_pillars || []}
            perCommitments={statsData?.per_commitments || []}
            commonKey="actions_count"
            title="Distributions des actions par piliers et engagements"
          />
        </Box>
        <Box bg="white" borderWidth="1px" p="4" rounded="md">
          <DonutsChartsDistribution
            perPillars={statsData?.per_pillars || []}
            perCommitments={statsData?.per_commitments || []}
            commonKey="indicators_count"
            title="Distributions des indicateurs par piliers et engagements"
          />
        </Box>
      </SimpleGrid>
    </>
  );
};

export default DashboardMainView;
